import { TZDate } from "@date-fns/tz";
import { bisector } from "d3-array";
import { timeFormat, utcFormat } from "d3-time-format";
import { get } from "svelte/store";

import { user } from "$lib/user";

export function computeTooltipLine(
  xPos: number,
  yPos: number,
  reverseTooltip: boolean,
) {
  const x2 = xPos + (reverseTooltip ? -16 : 16);
  let y2 = 12 + (yPos < 24 ? 30 : 0);

  if (yPos > y2) {
    y2 += Math.min(9, 0.3 * (yPos - y2));
  }
  if (yPos < y2) {
    y2 -= Math.min(9, 0.3 * (y2 - yPos));
  }

  const dx = x2 - xPos;
  const dy = y2 - yPos;

  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < 16) {
    return "";
  }

  const x1 = xPos + (dx * 12) / length + (reverseTooltip ? -2 : 2);
  const y1 = yPos + (dy * 12) / length;

  return `M${x1},${y1} L${x2},${y2}`;
}

export function computeTextDims(
  computeTextEl: SVGElement | undefined,
  text: string,
  classes: string,
  styles: CSSStyleDeclaration,
) {
  if (!computeTextEl) {
    return { width: 0, height: 0 };
  }
  const node = document.createElementNS("http://www.w3.org/2000/svg", "text");
  node.setAttribute("class", classes);

  for (const key in styles) {
    node.style[key] = styles[key];
  }

  node.textContent = text;
  computeTextEl.appendChild(node);
  const width = node.getBBox().width;
  const height = node.getBBox().height;
  computeTextEl.removeChild(node);
  return { width, height };
}

export function getTimeFormat(displayUtc: boolean, duration: number) {
  if (displayUtc) {
    return (date: Date) => {
      const format = utcFormat(
        duration ? "%a %b %-d, %H:%M:%S" : "%a %b %-d, %H:%M",
      );
      return format(date);
    };
  }

  const u = get(user);
  const timezone = (u?.logged_in && u?.timezone) || undefined;

  return (date: Date) => {
    const localDate = timezone ? new TZDate(date, timezone) : date;

    const format = timeFormat(
      duration < 300 * 1000 ? "%a %b %-d, %I:%M:%S %p" : "%a %b %-d, %I:%M %p",
    );
    return format(localDate);
  };
}

/**
 * Gets the index of the closest data point to a hover position
 * @param data Array of data points
 * @param hoverX The x-coordinate of the hover position
 * @param accessor Function to access the value to bisect on
 * @param roundMode Whether to round down or round to the closest value
 * @returns The index of the closest data point, or undefined if not hovering
 */
export function getHoverIndex<T, U>(
  data: Array<T>,
  hoverX: U | undefined,
  accessor: (d: T) => U,
  roundMode: "down" | "closest",
): number | undefined {
  if (!hoverX || !data.length) return undefined;
  const bisect = bisector(accessor);
  return roundMode === "down"
    ? Math.max(0, bisect.left(data, hoverX) - 1)
    : bisect.center(data, hoverX);
}

export type Series<T> = {
  values: Array<T>;
  name: string;
  color: string;
};
